/**
 * Fair Settlement Calculator Section
 * Monte Carlo-based mediation analysis between adjuster and attorney positions
 * 
 * Purpose: Provide a statistically fair settlement value that bridges
 * the gap between conservative (adjuster) and aggressive (attorney) positions.
 * 
 * Features:
 * - Settlement range visualization (Low → Fair → High)
 * - Monte Carlo statistics with confidence intervals
 * - Gap bridge analysis ("If you fix X, claim increases by $Y")
 * - Settlement scenarios with recommendations
 */

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  Scale,
  TrendingUp, 
  DollarSign, 
  Target,
  RefreshCw,
  ChevronDown,
  ChevronUp,
  CheckCircle2,
  ArrowRight,
  Info,
  BarChart3
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

interface FairSettlementProps {
  reportId: string;
  getAuthHeader: () => { Authorization: string };
  formatCurrency: (amount?: number) => string;
}

interface SettlementRange {
  conservative: number;
  fair: number;
  aggressive: number;
}

interface MonteCarloStats {
  iterations: number;
  mean: number;
  median: number;
  std_dev: number;
  p10: number;
  p25: number;
  p50: number;
  p75: number;
  p90: number;
  min: number;
  max: number;
  distribution_sample: number[];
}

interface GapBridgeItem {
  gap_id: number;
  title: string;
  description: string;
  severity?: string;
  category?: string;
  confidence?: string;
  current_impact: number;
  if_addressed: {
    value_increase: number;
    new_claim_value: number;
    percentage_increase: number;
  };
  how_to_address: string;
  evidence_needed?: string[];
  source: string;
  cumulative_claim: number;
}

interface Scenario {
  name: string;
  value: number;
  description: string;
  position: string;
  color: string;
  recommended?: boolean;
}

interface FairSettlementAnalysis {
  success: boolean;
  cached: boolean;
  analysis_id?: string;
  analyzed_at?: string;
  fair_settlement_value?: number;
  settlement_range?: SettlementRange;
  confidence_interval_95?: { low: number; high: number };
  adjuster_value?: number;
  attorney_value?: number;
  base_claim?: number;
  monte_carlo?: MonteCarloStats;
  gap_bridge?: GapBridgeItem[];
  scenarios?: Scenario[];
  executive_summary?: string;
  error?: string;
}

export default function FairSettlementSection({
  reportId,
  getAuthHeader,
  formatCurrency
}: FairSettlementProps) {
  const [analysis, setAnalysis] = useState<FairSettlementAnalysis | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    scenarios: true,
    gapBridge: false,
    monteCarlo: false
  });

  const fetchAnalysis = async (forceRefresh = false) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/v1/mediation/calculate/${reportId}?force_refresh=${forceRefresh}`,
        {},
        { headers: getAuthHeader() }
      );
      setAnalysis(response.data);
    } catch (err: any) {
      console.error('Fair settlement calculation failed:', err);
      setError(err.response?.data?.detail || 'Failed to calculate fair settlement');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAnalysis();
  }, [reportId]);

  const toggleSection = (section: string) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const getScenarioColorClass = (color: string) => {
    const colorMap: Record<string, string> = {
      red: 'bg-red-50 border-red-300 text-red-800',
      amber: 'bg-amber-50 border-amber-300 text-amber-800',
      green: 'bg-emerald-50 border-emerald-300 text-emerald-800',
      blue: 'bg-blue-50 border-blue-300 text-blue-800',
      purple: 'bg-purple-50 border-purple-300 text-purple-800',
      emerald: 'bg-teal-50 border-teal-300 text-teal-800'
    };
    return colorMap[color] || 'bg-gray-50 border-gray-300 text-gray-800';
  };

  if (loading && !analysis) {
    return (
      <div className="bg-white rounded-xl shadow-sm border-2 border-amber-200 p-6 mb-6">
        <div className="flex items-center gap-3 mb-4">
          <Scale className="w-6 h-6 text-amber-600" />
          <h2 className="text-xl font-bold text-gray-900">Fair Settlement Calculator</h2>
        </div>
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-10 w-10 border-b-2 border-amber-600 mb-4"></div>
            <p className="text-gray-600">Running Monte Carlo simulation...</p>
            <p className="text-sm text-gray-500 mt-1">2,000+ iterations for statistical accuracy</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-xl shadow-sm border-2 border-amber-200 p-6 mb-6">
        <div className="flex items-center gap-3 mb-4">
          <Scale className="w-6 h-6 text-amber-600" />
          <h2 className="text-xl font-bold text-gray-900">Fair Settlement Calculator</h2>
        </div>
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">{error}</p>
          <button
            onClick={() => fetchAnalysis(true)}
            className="mt-3 px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 text-sm"
          >
            Retry Calculation
          </button>
        </div>
      </div>
    );
  }

  if (!analysis) return null;

  const {
    fair_settlement_value,
    settlement_range,
    confidence_interval_95,
    adjuster_value,
    attorney_value,
    base_claim,
    monte_carlo,
    gap_bridge,
    scenarios,
    executive_summary,
    cached,
    analyzed_at
  } = analysis;

  // Calculate position percentage for the fair settlement on the scale
  const getPositionPercent = () => {
    if (!adjuster_value || !attorney_value || !fair_settlement_value) return 50;
    const range = attorney_value - adjuster_value;
    if (range === 0) return 50;
    return Math.min(100, Math.max(0, ((fair_settlement_value - adjuster_value) / range) * 100));
  };

  // Calculate total gap value
  const totalGapValue = gap_bridge?.reduce((sum, gap) => sum + gap.if_addressed.value_increase, 0) || 0;

  return (
    <div className="bg-white rounded-xl shadow-sm border-2 border-amber-200 p-6 mb-6" data-testid="fair-settlement-section">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-amber-100 rounded-lg">
            <Scale className="w-6 h-6 text-amber-600" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-gray-900">Fair Settlement Calculator</h2>
            <p className="text-sm text-gray-500">
              Monte Carlo Analysis • {cached ? '(Cached)' : 'Fresh'} • {analyzed_at ? new Date(analyzed_at).toLocaleString() : ''}
            </p>
          </div>
        </div>
        <button
          onClick={() => fetchAnalysis(true)}
          disabled={loading}
          className="flex items-center gap-2 px-4 py-2 text-amber-600 hover:bg-amber-50 rounded-lg transition"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          Recalculate
        </button>
      </div>

      {/* Executive Summary */}
      <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl p-6 mb-6 border border-amber-100">
        <div className="flex items-start gap-4 mb-6">
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Statistical Fair Settlement</h3>
            <p className="text-gray-700 text-sm">{executive_summary}</p>
          </div>
          <div className="text-right">
            <p className="text-3xl font-bold text-amber-700">{formatCurrency(fair_settlement_value)}</p>
            <p className="text-sm text-gray-500">Recommended (P50)</p>
            {monte_carlo && (
              <p className="text-xs text-gray-400 mt-1">
                {monte_carlo.iterations.toLocaleString()} iterations
              </p>
            )}
          </div>
        </div>

        {/* Settlement Scale Visualization */}
        <div className="bg-white rounded-xl p-4 border border-amber-200">
          <p className="text-sm font-semibold text-gray-700 mb-3">Settlement Range</p>
          
          {/* Scale Bar */}
          <div className="relative h-8 mb-2">
            {/* Background gradient from red to green */}
            <div className="absolute inset-0 rounded-full bg-gradient-to-r from-red-200 via-amber-200 to-green-200"></div>
            
            {/* Fair Settlement Marker */}
            <div 
              className="absolute top-0 h-full flex flex-col items-center transition-all duration-500"
              style={{ left: `${getPositionPercent()}%`, transform: 'translateX(-50%)' }}
            >
              <div className="w-1 h-full bg-amber-600 rounded"></div>
              <div className="absolute -top-6 bg-amber-600 text-white text-xs px-2 py-1 rounded font-semibold whitespace-nowrap">
                Fair: {formatCurrency(fair_settlement_value)}
              </div>
            </div>
          </div>

          {/* Labels */}
          <div className="flex justify-between text-xs">
            <div className="text-red-700">
              <p className="font-semibold">Adjuster (Low)</p>
              <p>{formatCurrency(adjuster_value)}</p>
            </div>
            <div className="text-center text-gray-600">
              <p className="font-semibold">Base Claim</p>
              <p>{formatCurrency(base_claim)}</p>
            </div>
            <div className="text-right text-green-700">
              <p className="font-semibold">Attorney (High)</p>
              <p>{formatCurrency(attorney_value)}</p>
            </div>
          </div>
        </div>

        {/* Key Metrics */}
        <div className="grid grid-cols-4 gap-4 mt-4">
          <div className="text-center bg-white rounded-lg p-3 border border-amber-100">
            <p className="text-xs text-gray-500">Conservative (P25)</p>
            <p className="text-lg font-bold text-amber-600">{formatCurrency(settlement_range?.conservative)}</p>
          </div>
          <div className="text-center bg-white rounded-lg p-3 border border-amber-100">
            <p className="text-xs text-gray-500">Fair (P50)</p>
            <p className="text-lg font-bold text-amber-700">{formatCurrency(settlement_range?.fair)}</p>
          </div>
          <div className="text-center bg-white rounded-lg p-3 border border-amber-100">
            <p className="text-xs text-gray-500">Aggressive (P75)</p>
            <p className="text-lg font-bold text-amber-600">{formatCurrency(settlement_range?.aggressive)}</p>
          </div>
          <div className="text-center bg-white rounded-lg p-3 border border-amber-100">
            <p className="text-xs text-gray-500">95% CI Range</p>
            <p className="text-sm font-bold text-gray-700">
              {formatCurrency(confidence_interval_95?.low)} - {formatCurrency(confidence_interval_95?.high)}
            </p>
          </div>
        </div>
      </div>

      {/* Settlement Scenarios */}
      {scenarios && scenarios.length > 0 && (
        <div className="mb-6">
          <button
            onClick={() => toggleSection('scenarios')}
            className="w-full flex items-center justify-between p-4 bg-blue-50 rounded-lg hover:bg-blue-100 transition"
          >
            <div className="flex items-center gap-3">
              <Target className="w-5 h-5 text-blue-600" />
              <span className="font-semibold text-blue-900">Settlement Scenarios</span>
            </div>
            {expandedSections.scenarios ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
          </button>
          
          {expandedSections.scenarios && (
            <div className="mt-3 space-y-3">
              {scenarios.map((scenario, idx) => (
                <div
                  key={idx}
                  className={`p-4 rounded-lg border-2 ${getScenarioColorClass(scenario.color)} ${scenario.recommended ? 'ring-2 ring-amber-400' : ''}`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h4 className="font-semibold">{scenario.name}</h4>
                        {scenario.recommended && (
                          <span className="px-2 py-0.5 bg-amber-500 text-white text-xs rounded-full font-semibold">
                            RECOMMENDED
                          </span>
                        )}
                      </div>
                      <p className="text-sm opacity-80">{scenario.description}</p>
                    </div>
                    <p className="text-xl font-bold ml-4">{formatCurrency(scenario.value)}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Gap Bridge Analysis */}
      {gap_bridge && gap_bridge.length > 0 && (
        <div className="mb-6">
          <button
            onClick={() => toggleSection('gapBridge')}
            className="w-full flex items-center justify-between p-4 bg-green-50 rounded-lg hover:bg-green-100 transition"
          >
            <div className="flex items-center gap-3">
              <DollarSign className="w-5 h-5 text-green-600" />
              <span className="font-semibold text-green-900">
                Gap Bridge Analysis: {formatCurrency(totalGapValue)} Potential Recovery
              </span>
            </div>
            {expandedSections.gapBridge ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
          </button>
          
          {expandedSections.gapBridge && (
            <div className="mt-3 space-y-4">
              <p className="text-sm text-gray-600 px-2">
                Address these gaps to increase claim value. Shows cumulative impact.
              </p>
              
              {gap_bridge.map((gap, idx) => (
                <div
                  key={idx}
                  className="p-4 rounded-lg bg-white border border-green-200 hover:border-green-400 transition"
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center text-green-700 font-bold text-sm">
                        {gap.gap_id}
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900">{gap.title}</h4>
                        <div className="flex items-center gap-2 mt-1">
                          <span className={`text-xs px-2 py-0.5 rounded ${
                            gap.source === 'adjuster_finding' 
                              ? 'bg-red-100 text-red-700' 
                              : 'bg-green-100 text-green-700'
                          }`}>
                            {gap.source === 'adjuster_finding' ? 'Adjuster Finding' : 'Unclaimed Loss'}
                          </span>
                          {gap.confidence && (
                            <span className={`text-xs ${
                              gap.confidence === 'HIGH' ? 'text-green-600' :
                              gap.confidence === 'MEDIUM' ? 'text-amber-600' : 'text-red-600'
                            }`}>
                              {gap.confidence} confidence
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-lg font-bold text-green-600">+{formatCurrency(gap.if_addressed.value_increase)}</p>
                      <p className="text-xs text-gray-500">+{gap.if_addressed.percentage_increase}%</p>
                    </div>
                  </div>

                  <p className="text-sm text-gray-600 mb-3">{gap.description}</p>

                  <div className="bg-green-50 rounded-lg p-3 mb-3">
                    <p className="text-xs font-semibold text-green-700 mb-1">If Addressed:</p>
                    <div className="flex items-center gap-2 text-sm">
                      <span className="text-gray-600">{formatCurrency(base_claim)}</span>
                      <ArrowRight className="w-4 h-4 text-green-600" />
                      <span className="font-bold text-green-700">{formatCurrency(gap.cumulative_claim)}</span>
                    </div>
                  </div>

                  <div className="text-sm">
                    <p className="font-semibold text-gray-700 mb-1">How to Address:</p>
                    <p className="text-gray-600">{gap.how_to_address}</p>
                  </div>

                  {gap.evidence_needed && gap.evidence_needed.length > 0 && (
                    <div className="mt-3 pt-3 border-t border-green-100">
                      <p className="text-xs font-semibold text-gray-600 mb-1">Evidence Needed:</p>
                      <div className="flex flex-wrap gap-1">
                        {gap.evidence_needed.map((doc, i) => (
                          <span key={i} className="text-xs px-2 py-0.5 bg-gray-100 rounded text-gray-600">
                            {doc}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}

              {/* Summary Banner */}
              <div className="bg-gradient-to-r from-green-100 to-emerald-100 rounded-lg p-4 border border-green-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-semibold text-green-900">Total Potential Recovery</p>
                    <p className="text-sm text-green-700">If all {gap_bridge.length} gaps are addressed</p>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-bold text-green-700">+{formatCurrency(totalGapValue)}</p>
                    <p className="text-sm text-green-600">
                      New Total: {formatCurrency((base_claim || 0) + totalGapValue)}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Monte Carlo Statistics */}
      {monte_carlo && (
        <div className="mb-6">
          <button
            onClick={() => toggleSection('monteCarlo')}
            className="w-full flex items-center justify-between p-4 bg-purple-50 rounded-lg hover:bg-purple-100 transition"
          >
            <div className="flex items-center gap-3">
              <BarChart3 className="w-5 h-5 text-purple-600" />
              <span className="font-semibold text-purple-900">
                Monte Carlo Statistics ({monte_carlo.iterations.toLocaleString()} iterations)
              </span>
            </div>
            {expandedSections.monteCarlo ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
          </button>
          
          {expandedSections.monteCarlo && (
            <div className="mt-3 bg-purple-50 rounded-lg p-4 border border-purple-200">
              <div className="grid grid-cols-4 gap-4 mb-4">
                <div className="bg-white rounded-lg p-3 text-center">
                  <p className="text-xs text-gray-500">Mean</p>
                  <p className="text-lg font-bold text-purple-700">{formatCurrency(monte_carlo.mean)}</p>
                </div>
                <div className="bg-white rounded-lg p-3 text-center">
                  <p className="text-xs text-gray-500">Median (P50)</p>
                  <p className="text-lg font-bold text-purple-700">{formatCurrency(monte_carlo.median)}</p>
                </div>
                <div className="bg-white rounded-lg p-3 text-center">
                  <p className="text-xs text-gray-500">Std Deviation</p>
                  <p className="text-lg font-bold text-purple-700">{formatCurrency(monte_carlo.std_dev)}</p>
                </div>
                <div className="bg-white rounded-lg p-3 text-center">
                  <p className="text-xs text-gray-500">Range</p>
                  <p className="text-sm font-bold text-purple-700">
                    {formatCurrency(monte_carlo.min)} - {formatCurrency(monte_carlo.max)}
                  </p>
                </div>
              </div>

              {/* Percentiles */}
              <div className="bg-white rounded-lg p-4">
                <p className="text-sm font-semibold text-gray-700 mb-3">Percentile Distribution</p>
                <div className="space-y-2">
                  {[
                    { label: 'P10 (Very Conservative)', value: monte_carlo.p10, color: 'bg-red-200' },
                    { label: 'P25 (Conservative)', value: monte_carlo.p25, color: 'bg-orange-200' },
                    { label: 'P50 (Fair / Median)', value: monte_carlo.p50, color: 'bg-amber-300' },
                    { label: 'P75 (Aggressive)', value: monte_carlo.p75, color: 'bg-yellow-200' },
                    { label: 'P90 (Very Aggressive)', value: monte_carlo.p90, color: 'bg-green-200' }
                  ].map((item, idx) => (
                    <div key={idx} className="flex items-center gap-3">
                      <div className={`w-24 text-xs text-gray-600`}>{item.label}</div>
                      <div className="flex-1 bg-gray-100 rounded-full h-6 relative">
                        <div 
                          className={`${item.color} h-full rounded-full`}
                          style={{ 
                            width: `${((item.value - monte_carlo.min) / (monte_carlo.max - monte_carlo.min)) * 100}%` 
                          }}
                        ></div>
                      </div>
                      <div className="w-24 text-right text-sm font-semibold text-gray-700">
                        {formatCurrency(item.value)}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Methodology Note */}
              <div className="mt-4 p-3 bg-purple-100 rounded-lg text-xs text-purple-800">
                <div className="flex items-start gap-2">
                  <Info className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <div>
                    <strong>Methodology:</strong> This Monte Carlo simulation runs {monte_carlo.iterations.toLocaleString()} iterations 
                    with multi-variable perturbation including revenue trend (±15%), seasonality factors (±10%), 
                    expense continuation rates (±20%), and recovery period assumptions (±15%). The P50 (median) 
                    represents the statistically most likely fair settlement value.
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Info Banner */}
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <Info className="w-5 h-5 text-amber-600 mt-0.5" />
          <div className="text-sm text-amber-800">
            <p className="font-semibold mb-1">About Fair Settlement Calculator</p>
            <p>
              This analysis uses Monte Carlo simulation to find a statistically fair settlement value 
              between the adjuster's conservative position and the attorney's aggressive position. 
              The P50 (median) value represents where 50% of simulation outcomes fell above and below, 
              making it a defensible "middle ground" for mediation discussions.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
